import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { useMediaQuery } from '@uidotdev/usehooks'
import type { ReactNode } from 'react'

/**
 * Settings beside a large storefront preview, with a draggable divider, on wide screens. Narrower
 * screens show only the settings (`contained`) or stack the preview under them (`natural`).
 */
export function WebsiteEditorSplitLayout({
  settingsPanel,
  previewPanel,
  autoSaveId,
  defaultSettingsSize = 34,
  mobileLayout = 'contained',
}: {
  settingsPanel: ReactNode
  previewPanel: ReactNode
  autoSaveId?: string
  defaultSettingsSize?: number
  mobileLayout?: 'contained' | 'natural'
}) {
  const isWideScreen = useMediaQuery('(min-width: 1280px)')

  if (!isWideScreen) {
    if (mobileLayout === 'natural') {
      return (
        <div className="min-w-0 space-y-4">
          <div>{settingsPanel}</div>
          <div className="h-[min(48rem,calc(100dvh-7rem))] min-h-[34rem] pb-4">{previewPanel}</div>
        </div>
      )
    }

    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">{settingsPanel}</div>
    )
  }

  return (
    <ResizablePanelGroup direction="horizontal" autoSaveId={autoSaveId} className="h-full min-h-0">
      <ResizablePanel
        defaultSize={defaultSettingsSize}
        minSize={28}
        maxSize={48}
        className="min-w-[22rem]"
      >
        <div className="h-full min-h-0 pr-2">{settingsPanel}</div>
      </ResizablePanel>

      <ResizableHandle
        withHandle
        className="mx-1 w-2 rounded-full bg-transparent transition-colors hover:bg-primary/15"
      />

      <ResizablePanel defaultSize={100 - defaultSettingsSize} minSize={44}>
        <div className="h-full min-h-0 pl-2">{previewPanel}</div>
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}
